import type {
  AssetCategory,
  AssetCondition,
  AssetCurrency,
  AssetKind,
  AssetLocation,
  AssetStatus,
  RestaurantAsset,
} from '../types';

export const ASSET_CATEGORIES: { id: AssetCategory; label: string }[] = [
  { id: 'kitchen_equipment', label: 'Kitchen equipment' },
  { id: 'tableware', label: 'Tableware' },
  { id: 'utensils', label: 'Utensils' },
  { id: 'uniforms', label: 'Uniforms' },
  { id: 'other', label: 'Other' },
];

export const ASSET_LOCATIONS: { id: AssetLocation; label: string }[] = [
  { id: 'kitchen', label: 'Kitchen' },
  { id: 'dining', label: 'Dining' },
  { id: 'bar', label: 'Bar' },
  { id: 'storage', label: 'Storage' },
  { id: 'other', label: 'Other' },
];

export const ASSET_STATUSES: { id: AssetStatus; label: string }[] = [
  { id: 'in_use', label: 'In use' },
  { id: 'stored', label: 'Stored' },
  { id: 'replace', label: 'Replace' },
  { id: 'written_off', label: 'Written off' },
];

export const ASSET_KINDS: { id: AssetKind; label: string }[] = [
  { id: 'quantity', label: 'Quantity line' },
  { id: 'tagged', label: 'Tagged item' },
];

export const ASSET_CURRENCIES: { id: AssetCurrency; label: string }[] = [
  { id: 'NZD', label: 'NZD' },
  { id: 'INR', label: 'INR' },
  { id: 'USD', label: 'USD' },
  { id: 'AUD', label: 'AUD' },
  { id: 'EUR', label: 'EUR' },
  { id: 'GBP', label: 'GBP' },
];

export const ASSET_CONDITIONS: { id: AssetCondition; label: string }[] = [
  { id: 'new', label: 'New' },
  { id: 'used', label: 'Used' },
  { id: 'lease', label: 'Lease' },
];

export function assetCategoryLabel(id: AssetCategory): string {
  return ASSET_CATEGORIES.find((c) => c.id === id)?.label ?? id;
}

export function assetLocationLabel(id: AssetLocation): string {
  return ASSET_LOCATIONS.find((l) => l.id === id)?.label ?? id;
}

export function assetStatusLabel(id: AssetStatus): string {
  return ASSET_STATUSES.find((s) => s.id === id)?.label ?? id;
}

export function assetConditionLabel(id: AssetCondition | undefined): string {
  if (!id) return '';
  return ASSET_CONDITIONS.find((c) => c.id === id)?.label ?? id;
}

export function formatAssetCost(amount?: number, currency?: AssetCurrency): string {
  if (amount == null || Number.isNaN(amount)) return '';
  const code = currency || 'NZD';
  return `${code} ${amount.toFixed(2)}`;
}

export function isAssetLowCount(asset: RestaurantAsset): boolean {
  if (asset.kind !== 'quantity') return false;
  if (asset.replaceBelow == null || Number.isNaN(asset.replaceBelow)) return false;
  return asset.quantity < asset.replaceBelow;
}

export function categoryQuantityTotals(
  assets: RestaurantAsset[],
): { category: AssetCategory; label: string; quantity: number }[] {
  return ASSET_CATEGORIES.map((cat) => ({
    category: cat.id,
    label: cat.label,
    quantity: assets
      .filter((a) => a.category === cat.id && a.status !== 'written_off')
      .reduce((sum, a) => sum + (Number(a.quantity) || 0), 0),
  })).filter((row) => row.quantity > 0);
}

export interface AssetBulkRow {
  key: string;
  kind: AssetKind;
  name: string;
  category: AssetCategory;
  location: AssetLocation;
  quantity: string;
  replaceBelow: string;
  assetTag: string;
  unitCost: string;
  unitCostCurrency: AssetCurrency;
  condition: AssetCondition;
  status: AssetStatus;
}

export function emptyAssetBulkRow(seed?: Partial<AssetBulkRow>): AssetBulkRow {
  return {
    kind: 'quantity',
    name: '',
    category: 'kitchen_equipment',
    location: 'kitchen',
    quantity: '1',
    replaceBelow: '',
    assetTag: '',
    unitCost: '',
    unitCostCurrency: 'NZD',
    condition: 'new',
    status: 'in_use',
    ...seed,
    key: seed?.key ?? `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  };
}

/** Returns a new asset when the row has a name; otherwise null. */
export function restaurantAssetFromBulkRow(row: AssetBulkRow): RestaurantAsset | null {
  const name = row.name.trim();
  if (!name) return null;
  const qty = Number(row.quantity);
  const quantity = row.kind === 'tagged' ? 1 : Number.isFinite(qty) && qty >= 0 ? qty : 0;
  const replaceRaw = row.replaceBelow.trim();
  const replaceBelow = replaceRaw === '' ? undefined : Number(replaceRaw);
  const costRaw = row.unitCost.trim();
  const unitCost = costRaw === '' ? undefined : Number(costRaw);
  const now = Date.now();
  return {
    id: '',
    kind: row.kind,
    name,
    category: row.category,
    location: row.location,
    quantity,
    replaceBelow:
      row.kind === 'quantity' && replaceBelow != null && Number.isFinite(replaceBelow) && replaceBelow >= 0
        ? replaceBelow
        : undefined,
    assetTag: row.kind === 'tagged' ? row.assetTag.trim() || undefined : undefined,
    unitCost: unitCost != null && Number.isFinite(unitCost) && unitCost >= 0 ? unitCost : undefined,
    unitCostCurrency:
      unitCost != null && Number.isFinite(unitCost) && unitCost >= 0 ? row.unitCostCurrency : undefined,
    condition: row.condition,
    status: row.status,
    createdAt: now,
    updatedAt: now,
  };
}
